import React, { useCallback, useMemo } from 'react';
import type { FC } from 'react';
import { getBackendPluginExtensionsDir } from '@wix/cli-astro-definitions';
import type { SelectGroup, SelectOption } from '@wix/cli-ui-kit';
import { SelectGroupsInput, InputState, TextInput } from '@wix/cli-ui-kit';
import type { SupportedServicePlugin } from '@wix/cli-astro-definitions';
import { ComponentType } from '@wix/dev-center-client';
import { useProjectModel } from '../../../model.js';
import { useWixCliAstroBi } from '../../../bi/index.js';
import { useWizardStep } from '../Wizard.js';
import {
  createServicePluginValidation,
  useValidation,
} from '../../../dev-center/components/validations/index.js';

export const SelectServicePluginStep: FC<{
  typeOption?: SelectOption<SupportedServicePlugin>;
  onSubmit: (value: SelectOption<SupportedServicePlugin>) => void;
}> = ({ onSubmit, typeOption }) => {
  const bi = useWixCliAstroBi();
  const { isActive, goToNextStep } = useWizardStep();

  const generateServicePluginOptions: Array<
    SelectGroup<SupportedServicePlugin>
  > = useMemo(
    () => [
      {
        title: 'Stores',
        items: [
          {
            title: 'Ecom Additional Fees',
            value: ComponentType.ECOM_ADDITIONAL_FEES,
          },
          {
            title: 'Ecom Shipping Rates',
            value: ComponentType.ECOM_SHIPPING_RATES,
          },
          {
            title: 'Ecom Discount Triggers',
            value: ComponentType.ECOM_DISCOUNTS_TRIGGER,
          },
          {
            title: 'Ecom Validations',
            value: ComponentType.ECOM_VALIDATIONS,
          },
          {
            title: 'Ecom Payment Settings',
            value: ComponentType.ECOM_PAYMENT_SETTINGS,
          },
          {
            title: 'Ecom Gift Cards Provider',
            value: ComponentType.GIFT_CARDS_PROVIDER,
          },
        ],
      },
      {
        title: 'Bookings',
        items: [
          {
            title: 'Staff Sorting Provider',
            value: ComponentType.STAFF_SORTING_PROVIDER,
          },
          {
            title: 'Booking Policy Provider',
            value: ComponentType.BOOKING_POLICY_PROVIDER,
          },
          {
            title: 'Availability Time Slots Configuration Provider',
            value: ComponentType.AVAILABILITY_TIME_SLOTS_CONFIGURATION_PROVIDER,
          },
          {
            title: 'Bookings Validation Provider',
            value: ComponentType.BOOKINGS_VALIDATION_PROVIDER,
          },
          {
            title: 'Bookings Pricing Provider',
            value: ComponentType.BOOKINGS_PRICING_PROVIDER,
          },
        ],
      },
      {
        title: 'Realtime',
        items: [
          {
            title: 'Realtime Permissions Provider',
            value: ComponentType.REALTIME_PERMISSIONS_PROVIDER,
          },
        ],
      },
      {
        title: 'AI',
        items: [
          {
            title: 'Tools Provider',
            value: ComponentType.TOOLS_PROVIDER_CONFIG,
          },
        ],
      },
    ],
    []
  );

  const selectSupportedServicePlugin = useCallback(
    (supportedServicePlugin: SelectOption<SupportedServicePlugin>) => {
      bi.cliFlowStepAnswered({
        question: 'Which service plugin do you want to create',
        questionKey: 'generate_command.service_plugin.choose_type',
        answer: supportedServicePlugin.title,
      });
      onSubmit(supportedServicePlugin);
      goToNextStep();
    },
    [bi, onSubmit, goToNextStep]
  );

  return (
    <SelectGroupsInput<SupportedServicePlugin>
      value={
        !isActive && typeOption
          ? InputState.Submitted(typeOption)
          : InputState.NotSubmitted(typeOption)
      }
      label={'Which service plugin do you want to create'}
      groups={generateServicePluginOptions}
      onSubmit={selectSupportedServicePlugin}
      maxHeight={14}
    />
  );
};

export const ChooseNameStep: FC<{
  name?: string;
  type?: SupportedServicePlugin;
  onSubmit: (value: string) => void;
}> = ({ onSubmit, type, name }) => {
  const bi = useWixCliAstroBi();
  const { isActive, goToNextStep } = useWizardStep();
  const { model } = useProjectModel();

  const validateServicePluginFolder = useValidation(
    createServicePluginValidation(
      getBackendPluginExtensionsDir(model.projectFolder)
    )
  );

  return (
    <TextInput
      isActive={isActive}
      label={'How would you like to name your service plugin'}
      placeholder={name ?? 'my-service-plugin'}
      onSubmit={(value) => {
        bi.cliFlowStepAnswered({
          question: 'How would you like to name your service plugin',
          questionKey: 'generate_command.service_plugin.choose_name',
          answer: value,
          additionalInfo: { servicePluginType: type },
        });
        onSubmit(value);
        goToNextStep();
      }}
      validate={validateServicePluginFolder}
    />
  );
};
